import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getProjectApprovals, getMyCorrectionApprovals } from "../../../api/projectApprovalApi";
import { UserContext } from "../../../contexts/CurrentUserContext";
import Preloader from "../../Preloader/Preloader";
import AgreementApprovalList from "./AgreementApprovalList";
import { countMyPendingAgreements } from "./agreementApprovalHelpers";
import {
  countPendingEstimateApprovals,
  getApprovalStatusKind,
} from "./projectApprovalHelpers";
import "./ProjectApproval.css";

const TAB_LABELS = {
  estimate: "сметы",
  corrections: "корректировок",
  cp: "КП",
  plan: "плана",
  bonuses: "бонусов",
  agreements: "соглашений",
};

function ProjectApproval() {
  const navigate = useNavigate();
  const { userData } = useContext(UserContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(tabFromUrl || "estimate");
  const [approvals, setApprovals] = useState([]);
  const [correctionApprovals, setCorrectionApprovals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProject, setSelectedProject] = useState("all");
  const [selectedInitiator, setSelectedInitiator] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [sortOrder, setSortOrder] = useState("desc");
  const [agreementPendingCount, setAgreementPendingCount] = useState(0);

  useEffect(() => {
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl, activeTab]);

  const fetchApprovals = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [estimateData, correctionData] = await Promise.all([
        getProjectApprovals(),
        getMyCorrectionApprovals().catch(() => []),
      ]);
      setApprovals(estimateData);
      setCorrectionApprovals(Array.isArray(correctionData) ? correctionData : []);
    } catch (err) {
      setError("Ошибка при загрузке проектов для согласования");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  const fetchAgreementPendingCount = useCallback(async () => {
    if (!userData?.id) {
      setAgreementPendingCount(0);
      return;
    }
    try {
      const pendingCount = await countMyPendingAgreements(userData.id);
      setAgreementPendingCount(pendingCount);
    } catch {
      setAgreementPendingCount(0);
    }
  }, [userData?.id]);

  useEffect(() => {
    fetchAgreementPendingCount();
  }, [fetchAgreementPendingCount]);

  useEffect(() => {
    if (activeTab === "agreements") {
      fetchAgreementPendingCount();
    }
  }, [activeTab, fetchAgreementPendingCount]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedProject("all");
    setSelectedInitiator("all");
    setSelectedStatus("all");
    if (tab === "estimate") {
      setSearchParams({});
    } else {
      setSearchParams({ tab });
    }
  };

  const formatCurrency = (value) => {
    if (!value) return "0";
    return new Intl.NumberFormat("ru-RU").format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getStatusKind = getApprovalStatusKind;

  const getStatusLabel = (status) => {
    const kind = getStatusKind(status);
    if (kind === "approved") return "Согласован";
    if (kind === "rejected") return "Не согласован";
    if (kind === "cancelled") return "Отменен инициатором";
    return "На рассмотрении";
  };

  const getStatusIcon = (status) => {
    const kind = getStatusKind(status);
    if (kind === "approved") {
      return <span className="project-approval__status-icon approved">✓</span>;
    }
    if (kind === "pending") {
      return <span className="project-approval__status-icon pending">⏳</span>;
    }
    if (kind === "rejected") {
      return <span className="project-approval__status-icon rejected">✕</span>;
    }
    if (kind === "cancelled") {
      return <span className="project-approval__status-icon cancelled">✕</span>;
    }
    return null;
  };

  const matchesStatusFilter = (approval) => {
    if (selectedStatus === "all") return true;
    const overallKind = getStatusKind(approval.status);
    if (overallKind === "cancelled") {
      return selectedStatus === "cancelled";
    }
    if (overallKind === selectedStatus) return true;
    const stepKinds = (approval.steps || []).map((step) =>
      getStatusKind(step.status),
    );
    return stepKinds.includes(selectedStatus);
  };

  const uniqueProjects = Array.from(
    new Set(approvals.map((a) => a.project_name)),
  ).filter(Boolean);

  const uniqueInitiators = Array.from(
    new Set(approvals.map((a) => a.initiator_name)),
  ).filter(Boolean);

  const uniqueCorrectionProjects = Array.from(
    new Set(correctionApprovals.map((a) => a.project_name)),
  ).filter(Boolean);

  const uniqueCorrectionInitiators = Array.from(
    new Set(correctionApprovals.map((a) => a.initiator_name)),
  ).filter(Boolean);

  const filteredApprovals = approvals.filter((approval) => {
    if (
      selectedProject !== "all" &&
      approval.project_name !== selectedProject
    ) {
      return false;
    }
    if (
      selectedInitiator !== "all" &&
      approval.initiator_name !== selectedInitiator
    ) {
      return false;
    }
    if (!matchesStatusFilter(approval)) {
      return false;
    }
    return true;
  });

  const filteredCorrectionApprovals = correctionApprovals.filter((approval) => {
    if (
      selectedProject !== "all" &&
      approval.project_name !== selectedProject
    ) {
      return false;
    }
    if (
      selectedInitiator !== "all" &&
      approval.initiator_name !== selectedInitiator
    ) {
      return false;
    }
    if (!matchesStatusFilter(approval)) {
      return false;
    }
    return true;
  });

  const estimatePendingCount = countPendingEstimateApprovals(
    approvals,
    userData?.id,
  );
  const correctionPendingCount = countPendingEstimateApprovals(
    correctionApprovals,
    userData?.id,
  );

  const sortedApprovals = [...filteredApprovals].sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
  });
  const sortedCorrectionApprovals = [...filteredCorrectionApprovals].sort(
    (a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    },
  );

  const renderPlaceholder = () => (
    <div className="project-approval__empty">
      Раздел «{TAB_LABELS[activeTab]}» в разработке
    </div>
  );

  const renderEstimateTab = () => {
    if (isLoading) return <Preloader />;
    if (error) {
      return <div className="project-approval__error">{error}</div>;
    }

    return (
      <>
        <div className="project-approval__filters">
          <div className="project-approval__filter">
            <label className="project-approval__filter-label">
              Выбор проекта
            </label>
            <select
              className="project-approval__filter-select"
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
            >
              <option value="all">Все проекты</option>
              {uniqueProjects.map((project) => (
                <option key={project} value={project}>
                  {project}
                </option>
              ))}
            </select>
          </div>

          <div className="project-approval__filter">
            <label className="project-approval__filter-label">Инициатор</label>
            <select
              className="project-approval__filter-select"
              value={selectedInitiator}
              onChange={(e) => setSelectedInitiator(e.target.value)}
            >
              <option value="all">Все</option>
              {uniqueInitiators.map((initiator) => (
                <option key={initiator} value={initiator}>
                  {initiator}
                </option>
              ))}
            </select>
          </div>

          <div className="project-approval__filter">
            <label className="project-approval__filter-label">Статус</label>
            <select
              className="project-approval__filter-select"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="all">Все</option>
              <option value="pending">На рассмотрении</option>
              <option value="approved">Согласован</option>
              <option value="rejected">Не согласован</option>
              <option value="cancelled">Отменен инициатором</option>
            </select>
          </div>
        </div>

        {sortedApprovals.length === 0 ? (
          <div className="project-approval__empty">
            Нет сметы для согласования
          </div>
        ) : (
          <div className="project-approval__table-container">
            <table className="project-approval__table">
              <thead>
                <tr>
                  <th>Проект</th>
                  <th>Инициатор</th>
                  <th>Описание</th>
                  <th>Сумма</th>
                  <th>Статус</th>
                  <th>
                    <div className="project-approval__sort-header">
                      Дата отправки
                      <button
                        type="button"
                        className="project-approval__sort-button"
                        onClick={() =>
                          setSortOrder(sortOrder === "asc" ? "desc" : "asc")
                        }
                      >
                        {sortOrder === "asc" ? "↑" : "↓"}
                      </button>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedApprovals.map((approval) => (
                  <tr
                    key={approval.id}
                    className="project-approval__table-row"
                    onClick={() =>
                      navigate(
                        `/approvals/project-approval/${approval.project_id}/${approval.id}`,
                      )
                    }
                  >
                    <td>{approval.project_name || "—"}</td>
                    <td>{approval.initiator_name || "—"}</td>
                    <td className="project-approval__comment-cell">
                      {approval.comment?.trim() ? approval.comment : "—"}
                    </td>
                    <td>
                      {approval.total_price
                        ? `₸ ${formatCurrency(approval.total_price)}`
                        : "—"}
                    </td>
                    <td>
                      <div className="project-approval__status-list">
                        {getStatusKind(approval.status) === "cancelled" ? (
                          <div className="project-approval__status-item">
                            {getStatusIcon(approval.status)}
                            <span className="project-approval__status-text">
                              {getStatusLabel(approval.status)}
                            </span>
                          </div>
                        ) : approval.steps && approval.steps.length > 0 ? (
                          approval.steps.map((step, index) => (
                            <div
                              key={step.id || index}
                              className="project-approval__status-item"
                            >
                              <span className="project-approval__approver-name">
                                {step.approver_name || "Не указан"}:
                              </span>
                              {getStatusIcon(step.status)}
                              <span className="project-approval__status-text">
                                {getStatusLabel(step.status)}
                              </span>
                            </div>
                          ))
                        ) : (
                          <div className="project-approval__status-item">
                            {getStatusIcon(approval.status)}
                            <span className="project-approval__status-text">
                              {getStatusLabel(approval.status)}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="project-approval__date">
                        <span className="project-approval__date-icon">📅</span>
                        {formatDate(approval.date)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  };

  const renderCorrectionsTab = () => {
    if (isLoading) return <Preloader />;
    if (error) {
      return <div className="project-approval__error">{error}</div>;
    }

    return (
      <>
        <div className="project-approval__filters">
          <div className="project-approval__filter">
            <label className="project-approval__filter-label">
              Выбор проекта
            </label>
            <select
              className="project-approval__filter-select"
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
            >
              <option value="all">Все проекты</option>
              {uniqueCorrectionProjects.map((project) => (
                <option key={project} value={project}>
                  {project}
                </option>
              ))}
            </select>
          </div>

          <div className="project-approval__filter">
            <label className="project-approval__filter-label">Инициатор</label>
            <select
              className="project-approval__filter-select"
              value={selectedInitiator}
              onChange={(e) => setSelectedInitiator(e.target.value)}
            >
              <option value="all">Все</option>
              {uniqueCorrectionInitiators.map((initiator) => (
                <option key={initiator} value={initiator}>
                  {initiator}
                </option>
              ))}
            </select>
          </div>

          <div className="project-approval__filter">
            <label className="project-approval__filter-label">Статус</label>
            <select
              className="project-approval__filter-select"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="all">Все</option>
              <option value="pending">На рассмотрении</option>
              <option value="approved">Согласован</option>
              <option value="rejected">Не согласован</option>
              <option value="cancelled">Отменен инициатором</option>
            </select>
          </div>
        </div>

        {sortedCorrectionApprovals.length === 0 ? (
          <div className="project-approval__empty">
            Нет корректировок для согласования
          </div>
        ) : (
          <div className="project-approval__table-container">
            <table className="project-approval__table">
              <thead>
                <tr>
                  <th>Проект</th>
                  <th>Инициатор</th>
                  <th>Комментарий</th>
                  <th>Сумма заказчику</th>
                  <th>Статус</th>
                  <th>
                    <div className="project-approval__sort-header">
                      Дата отправки
                      <button
                        type="button"
                        className="project-approval__sort-button"
                        onClick={() =>
                          setSortOrder(sortOrder === "asc" ? "desc" : "asc")
                        }
                      >
                        {sortOrder === "asc" ? "↑" : "↓"}
                      </button>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedCorrectionApprovals.map((approval) => (
                  <tr
                    key={approval.id}
                    className="project-approval__table-row"
                    onClick={() =>
                      navigate(
                        `/approvals/project-approval/corrections/${approval.project_id}/${approval.id}`,
                      )
                    }
                  >
                    <td>{approval.project_name || "—"}</td>
                    <td>{approval.initiator_name || "—"}</td>
                    <td className="project-approval__comment-cell">
                      {approval.comment?.trim() ? approval.comment : "—"}
                    </td>
                    <td>
                      {approval.total_price
                        ? `₸ ${formatCurrency(approval.total_price)}`
                        : "—"}
                    </td>
                    <td>
                      <div className="project-approval__status-list">
                        {getStatusKind(approval.status) === "cancelled" ? (
                          <div className="project-approval__status-item">
                            {getStatusIcon(approval.status)}
                            <span className="project-approval__status-text">
                              {getStatusLabel(approval.status)}
                            </span>
                          </div>
                        ) : approval.steps && approval.steps.length > 0 ? (
                          approval.steps.map((step, index) => (
                            <div
                              key={step.id || index}
                              className="project-approval__status-item"
                            >
                              <span className="project-approval__approver-name">
                                {step.approver_name || "Не указан"}:
                              </span>
                              {getStatusIcon(step.status)}
                              <span className="project-approval__status-text">
                                {getStatusLabel(step.status)}
                              </span>
                            </div>
                          ))
                        ) : (
                          <div className="project-approval__status-item">
                            {getStatusIcon(approval.status)}
                            <span className="project-approval__status-text">
                              {getStatusLabel(approval.status)}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="project-approval__date">
                        <span className="project-approval__date-icon">📅</span>
                        {formatDate(approval.date)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="project-approval">
      <div className="project-approval__tabs">
        <button
          type="button"
          className={`project-approval__tab ${
            activeTab === "estimate" ? "active" : ""
          }`}
          onClick={() => handleTabChange("estimate")}
        >
          <span className="project-approval__tab-label">Смета</span>
          {estimatePendingCount > 0 && (
            <span className="project-approval__tab-badge">
              {estimatePendingCount}
            </span>
          )}
        </button>
        <button
          type="button"
          className={`project-approval__tab ${
            activeTab === "corrections" ? "active" : ""
          }`}
          onClick={() => handleTabChange("corrections")}
        >
          <span className="project-approval__tab-label">Корректировки</span>
          {correctionPendingCount > 0 && (
            <span className="project-approval__tab-badge">
              {correctionPendingCount}
            </span>
          )}
        </button>
        <button
          type="button"
          className={`project-approval__tab ${activeTab === "cp" ? "active" : ""}`}
          onClick={() => handleTabChange("cp")}
        >
          КП
        </button>
        <button
          type="button"
          className={`project-approval__tab ${
            activeTab === "plan" ? "active" : ""
          }`}
          onClick={() => handleTabChange("plan")}
        >
          План
        </button>
        <button
          type="button"
          className={`project-approval__tab ${
            activeTab === "bonuses" ? "active" : ""
          }`}
          onClick={() => handleTabChange("bonuses")}
        >
          Бонусы
        </button>
        <button
          type="button"
          className={`project-approval__tab ${
            activeTab === "agreements" ? "active" : ""
          }`}
          onClick={() => handleTabChange("agreements")}
        >
          <span className="project-approval__tab-label">Соглашения</span>
          {agreementPendingCount > 0 && (
            <span className="project-approval__tab-badge">
              {agreementPendingCount}
            </span>
          )}
        </button>
      </div>

      {activeTab === "estimate" && renderEstimateTab()}
      {activeTab === "corrections" && renderCorrectionsTab()}
      {activeTab === "agreements" && (
        <AgreementApprovalList onChanged={fetchAgreementPendingCount} />
      )}
      {(activeTab === "cp" ||
        activeTab === "plan" ||
        activeTab === "bonuses") &&
        renderPlaceholder()}
    </div>
  );
}

export default ProjectApproval;
